import { ComboboxField } from '@monorepo/shared/componentsV2/fields/ComboboxField';
import {
  MathUnit,
  NullableNumber,
  NullableString,
  NumericFieldResponse,
  NumericValue,
  SaveFieldValueRequest,
  SaveNumericValue,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { StyledInput } from '../../../styled/form';
import { FormValues } from './types';
import {
  getUnexpectedTypeErrorAndLogToSentry,
  getValidateFunction,
  numberFieldValueToFormInitialValue,
} from './utils';

const Container = styled.div`
  display: flex;
`;

const UnitsCombobox = styled(ComboboxField)`
  display: flex;
  flex-direction: column;
  width: 10rem;
  min-width: 7rem;
  margin-left: 0.5rem;

  & .MuiFormHelperText-root {
    display: none;
  }
`;

type EditNumericFieldValueProps = {
  className?: string;
  field: NumericFieldResponse;
  fieldValue?: NumericValue;
  isRequired: boolean;
  label: string;
  name: string;
  subText?: NullableString;
  disabled?: boolean;
  isReadOnly?: boolean;
  isLabelHidden?: boolean;
  isErrorTextHidden?: boolean;
  isSubtextHidden?: boolean;
  isCompact?: boolean;
  forceShowErrors?: boolean;
};

export function EditNumericFieldValue({
  className,
  field,
  fieldValue,
  isRequired,
  label,
  name,
  subText,
  disabled,
  isReadOnly,
  isLabelHidden,
  isErrorTextHidden,
  isSubtextHidden,
  isCompact,
  forceShowErrors,
}: EditNumericFieldValueProps) {
  const transformedNumericValue = useMemo(
    () => numberFieldValueToFormInitialValue(field, fieldValue),
    [field, fieldValue],
  );
  return (
    <Container className={className}>
      <StyledInput
        label={label}
        name={name}
        type="number"
        initialValue={transformedNumericValue.initialValue}
        validate={getValidateFunction(isRequired)}
        required={isRequired}
        subText={subText}
        disabled={disabled}
        isLabelHidden={isLabelHidden}
        isErrorTextHidden={isErrorTextHidden}
        isSubtextHidden={isSubtextHidden}
        isCompact={isCompact}
        forceShowErrors={forceShowErrors}
      />
      {field.units && (
        <UnitsCombobox
          options={MathUnit.getRelatedSelectableUnits(field.units)}
          initialValue={transformedNumericValue.initialUnits}
          label="Units"
          name={`${name}_unit`}
          isReadOnly={isReadOnly}
          isLabelHidden={isLabelHidden}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          isCompact={isCompact}
          forceShowErrors={forceShowErrors}
        />
      )}
    </Container>
  );
}

export function getNumericFieldValueFromForm(
  field: NumericFieldResponse,
  formValues: FormValues,
): SaveFieldValueRequest {
  const value = formValues[field.id] == null ? null : formValues[field.id];
  let parsedValue: NullableNumber;

  if (value === null) {
    parsedValue = value;
  } else if (typeof value === 'string') {
    parsedValue = parseFloat(value);
    if (Number.isNaN(parsedValue)) {
      throw getUnexpectedTypeErrorAndLogToSentry(field, value);
    }
  } else {
    throw getUnexpectedTypeErrorAndLogToSentry(field, value);
  }

  const givenUnits = formValues[`${field.id}_unit`];

  if (
    givenUnits != null &&
    !(typeof givenUnits === 'string' && MathUnit.isValid(givenUnits))
  ) {
    throw new Error(
      `Unexpected value provided for units of ${field.name} (${givenUnits}).`,
    );
  }

  const fieldValue: SaveNumericValue = {
    value: parsedValue,
    givenUnits: givenUnits || null,
  };
  return {
    fieldId: field.id,
    fieldValue,
  };
}
